import type { SavedProfileView, SavedSeatProfile } from '@tsr/shared-types';
import type { CameraView } from './cameraFly';
import type { BboxQuery } from './api';
import { saveHomeView as saveHomeViewLocal } from './homeView';

/**
 * Server-synced saved views (tracker #154 named regions + #155 home-view server
 * sync).
 *
 * Lifts the #149 home view off localStorage onto the server so it follows the
 * operator across devices, and adds operator-named region bboxes. The pattern
 * mirrors the layer-prefs server sync (prefs.ts): the server is the source of
 * truth (server wins on load), localStorage is the offline fallback that keeps
 * first-paint + the `H` keybind working with no round-trip and survives an
 * offline session. All calls degrade silently — a saved view must never break the
 * operator's map.
 */

/** A saved region as returned by the API (bbox the operator flies to / scopes a
 *  SITREP to). */
export interface SavedRegion {
  id: string;
  label: string;
  bbox: BboxQuery;
  created_at: string;
}

export interface SavedViewsData {
  home: CameraView | null;
  regions: SavedRegion[];
  profiles: SavedSeatProfile[];
}

/** GET the operator's server-side saved views. Non-blocking by design — the
 *  caller renders home from its localStorage cache first and reconciles this when
 *  it arrives. Returns null on any error (offline / 5xx) so the caller keeps its
 *  local state with no UI disruption. */
export async function fetchSavedViews(signal?: AbortSignal): Promise<SavedViewsData | null> {
  try {
    const res = await fetch('/api/saved-views', { signal });
    if (!res.ok) return null;
    const body = (await res.json()) as Partial<SavedViewsData>;
    return {
      home: body.home ?? null,
      regions: Array.isArray(body.regions) ? body.regions : [],
      profiles: Array.isArray(body.profiles) ? body.profiles : [],
    };
  } catch {
    return null;
  }
}

/** Persist the home view to the server (server-wins cross-device) AND mirror it
 *  to localStorage so the offline fallback + first-paint framing stay correct.
 *  Fire-and-forget on the network leg; the local write always lands. */
export async function persistHomeView(view: CameraView): Promise<void> {
  saveHomeViewLocal(view);
  try {
    await fetch('/api/saved-views', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind: 'home', view }),
    });
  } catch {
    // Offline → localStorage remains the source of truth; the next "Set home"
    // re-attempts. Deliberately not requeued (no retry storm).
  }
}

/** Save (or replace a same-named) region. Returns the created row (with its id +
 *  created_at) so the caller can splice it into local state, or null on failure. */
export async function persistRegion(label: string, bbox: BboxQuery): Promise<SavedRegion | null> {
  try {
    const res = await fetch('/api/saved-views', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind: 'region', label, bbox }),
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { region?: SavedRegion };
    return body.region ?? null;
  } catch {
    return null;
  }
}

/** Save (or replace a same-named) seat profile (#187). Returns the created row
 *  (with its id + created_at) so the caller can splice it into local state, or
 *  null on failure. */
export async function persistSeatProfile(
  label: string,
  view: SavedProfileView,
): Promise<SavedSeatProfile | null> {
  try {
    const res = await fetch('/api/saved-views', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind: 'profile', label, view }),
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { profile?: SavedSeatProfile };
    return body.profile ?? null;
  } catch {
    return null;
  }
}

/** Delete a saved view by id. Returns true on success. */
export async function deleteSavedView(id: string): Promise<boolean> {
  try {
    const res = await fetch(`/api/saved-views/${id}`, { method: 'DELETE' });
    return res.ok;
  } catch {
    return false;
  }
}
